import { TaskShowItem } from '@apis/tasksApi.types';
import {
  FloorUnitCreatePayload,
  TransactionMarketing,
  TransactionsSpaceDetailsUpdateDTO,
} from '@apis/transactionsLettingsApi.types';
import { AddressPayload } from '@shared/components/update-address-drawer';
import { Company } from '@shared/models/company/company';
import { Contact } from '@shared/models/contact/contact';
import { IDisposalListItem } from '@shared/models/disposal/disposal';
import { FileItem } from '@shared/models/file';
import { IImageDetails } from '@shared/models/IImages';
import { IVideo } from '@shared/models/IVideo';
import { Requirement } from '@shared/models/requirement/requirement';
import { SavedViewType } from '@shared/models/saved_view/savedView';
import { ITag } from '@shared/models/tag/tag';
import { ITeam } from '@shared/models/team/team';
import {
  IEpc,
  IPublishToAtlas,
  IPublishToAtlasDetails,
  IPublishToRadius,
  TransactionLeaseSpace,
} from '@shared/models/transactions/transactions';
import { UserProfile } from '@shared/models/user';
import { ActivityIndexItem } from '@shared/types/common/activity';
import { PropertyItem } from '@shared/types/common/property';
import { IndexResponse, ListResponsePagination } from '@shared/types/services';

import { BulkActionFailed } from './bulkActionsApi.types';

export enum SaleTransactionModuleKeys {
  all = 'all',
  investment = 'investment',
  occupational = 'occupational',
}

export enum SaleTransactionRequestSegments {
  all = '',
  investment = '/investment',
  occupational = '/occupational',
}

export interface SaleTransactionsListItem {
  accuracy: string;
  address_line_1: string;
  address_line_2: string;
  address: string;
  assignees: string;
  breeam_rating: string | null;
  build_date: string | null;
  build_status: string | null;
  building_grade: string | null;
  buildingTypes: string[];
  buildingTypesString: string;
  client_requested_confidentiality: string;
  completion_date: string | null;
  created_at: string;
  created_by: string;
  demise: string;
  entire_property: boolean;
  epc: string;
  external_ref: string | null;
  fitout_comment: string | null;
  fitout_concept: string | null;
  fitout_condition: string | null;
  freeholders: string | null;
  gross_initial_yield: number | null;
  id: number;
  image: string | null;
  investment: string;
  lat: number | null;
  lng: number | null;
  measurement_type: string;
  nabers_energy: string | null;
  nabers_indoor: string | null;
  nabers_waste: string | null;
  nabers_water: string | null;
  nda_note: string | null;
  nda_signed: string;
  net_initial_yield: number | null;
  net_zero_carbon: string | null;
  our_acquisition: string;
  our_disposal: string;
  passing_rent_currency: string;
  passing_rent_metric: string | null;
  passing_rent: string;
  permitted_use_class: string;
  pertinent_information: string | null;
  planning_status: string | null;
  postcode: string;
  price_currency: string;
  price_psf: string;
  price: string;
  purchaser_represented_by: string;
  purchaser: string;
  reason_for_requirement_string: string;
  sectors_string: string;
  size_metric: string;
  size_value: string;
  size: number | null;
  source_text: string | null;
  source: string;
  teams: string;
  tenancy_comments: string | null;
  tenancy_status: string;
  town: string;
  transaction_status_string: string;
  transaction_status: number;
  transaction_type: string;
  type: string;
  units: string;
  updated_at: string;
  uprn: number | null;
  vendor_represented_by: string;
  vendor: string;
  wault_to_break: number | null;
  wault_to_expiry: number | null;
  well_rating: string | null;
}

export const saleTransactionsListNonFilterableParamKeys = ['assigned'] as const;

export const saleTransactionsListFilterGroupKeys = [
  ['assigned_users', 'assigned_users_string'],
  ['cap_val_from', 'cap_val_to', 'cap_val_curency'],
  ['companies', 'companies_string'],
  ['completion_date_from', 'completion_date_to'],
  ['contacts', 'contacts_string'],
  ['created_ago', 'created_ahead', 'created_from', 'created_to'],
  ['updated_ago', 'updated_ahead', 'updated_from', 'updated_to'],
  ['job_id', 'job_status'],
  ['lot_size_currency', 'lot_size_from', 'lot_size_to'],
  ['niy_from', 'niy_to'],
  ['price_currency', 'price_from', 'price_to'],
  ['wault_to_expiry_from', 'wault_to_expiry_to'],
] as const;

export const saleTransactionsListFilterParamKeys = [
  ...saleTransactionsListFilterGroupKeys.flat(),
  'building_types',
  'investment',
  'our_instructions',
  'published_to_atlas',
  'published_to_radius',
  'sale_types',
  'search',
  'sources',
  'status',
  'tags',
  'teams',
  'tenancy_status',
  'updated_at',
] as const;

export const saleTransactionsListParamKeys = [
  ...saleTransactionsListFilterParamKeys,
  'limit',
  'order',
  'page',
] as const;
export type SaleTransactionsListParams = PartialQuery<typeof saleTransactionsListParamKeys>;

export interface SaleTransactionsListPayload extends QueryPayload<SaleTransactionsListParams> {
  requestSegment?: SaleTransactionRequestSegments;
  viewType?: SavedViewType;
}

export type SaleTransactionsListResponse = IndexResponse<SaleTransactionsListItem>;

export interface SaleTransactionsShowItem {
  accuracy: string | null;
  accuracy_string: string | null;
  amenities_specifications: KeyLabelValueRecord[];
  assigned_users: UserProfile[];
  assigned_users_string: string;
  acquisition: Requirement;
  branding: {
    main_colour: string;
    secondary_colour: string;
    tertiary_colour: string;
  };
  breeam_rating: null;
  brochures: FileItem[];
  building_grade: string | null;
  building_types: string[];
  cap_val_currency: string;
  cap_val: number | null;
  class_of_use: string[];
  client_requested_confidentiality: NumberBoolean | null;
  comments: any[];
  completion_date: string | null;
  condition: null;
  confidential: boolean;
  created_at: string;
  created_by: string;
  disposal: IDisposalListItem;
  disposals: IDisposalListItem[];
  entire_property: boolean;
  epc: IEpc;
  epcs: FileItem[];
  epc_exempt: boolean | null;
  epc_exempt_reason: number | null;
  estimated: NumberBoolean | null;
  external_ref: string | null;
  failed_bulk_action?: BulkActionFailed;
  floor_plans: any[];
  floor_unit_availability: any[];
  heads_of_terms: FileItem[];
  id: number;
  images: IImageDetails[];
  import_records: any[];
  internal_confidentiality_date: string | null;
  investment: NumberBoolean;
  lease_comment: string | null;
  lease_end: null;
  lease_expiry_month: number | null;
  lease_expiry_year: number | null;
  leased_spaces: TransactionLeaseSpace[];
  legal_fees_applicable: string | null;
  legal_fees_comment: string | null;
  logo: any[];
  marketing: TransactionMarketing;
  nda_comments: string | null;
  nda_signed: NumberBoolean | null;
  other_files: FileItem[];
  our_acquisition: boolean;
  our_disposal: boolean;
  private_files: FileItem[];
  passing_giy: number;
  passing_niy: number;
  passing_rent_currency: string;
  passing_rent: null;
  passing_rent_metric: Metric | null;
  pattern: null;
  pertinent_info: string | null;
  postcode: string;
  potential_epc: IEpc;
  transaction_notes: string | null;
  primary_address: string;
  property_name: string | null;
  property: PropertyItem | null;
  publish_to_atlas: IPublishToAtlas;
  publish_to_radius: IPublishToRadius;
  purchase_price_currency: string;
  purchase_price: number | null;
  purchaser_agent_companies: Company[];
  purchaser_agent_contacts: Contact[];
  purchaser_agent_users: UserProfile[];
  purchaser_companies: Company[];
  purchaser_contacts: Contact[];
  purchaser_solicitor_companies: Company[];
  purchaser_solicitor_contacts: Contact[];
  reason_for_requirement: string[];
  reason_for_requirement_string: string;
  internal_confidentiality: boolean;
  sale_type: number;
  secondary_address: string;
  sectors: string[];
  sectors_string: string;
  source_text: string | null;
  size_measure: string;
  size_metric: Metric | null;
  size_value: number | null;
  source: number | null;
  source_readable: string | null;
  status: null;
  tags: ITag[];
  tasks: TaskShowItem[];
  teams: ITeam[];
  tenancy_comment: string | null;
  transaction_status: number;
  transaction_status_string: string;
  updated_at: string;
  vat_applicable: string | null;
  vat_comment: string | null;
  vendor_agent_companies: Company[];
  vendor_agent_contacts: Contact[];
  vendor_agent_users: UserProfile[];
  vendor_companies: Company[];
  vendor_contacts: Contact[];
  vendor_solicitor_companies: Company[];
  vendor_solicitor_contacts: Contact[];
  videos: IVideo[];
  wault_to_break: number;
  wault_to_expiry: number;
}

export interface SaleTransactionsCreateDetails {
  agency_role?: number | null;
  assigned_users: Id[];
  atlas_publish_details?: Record<string, { enabled: boolean; title: string }> | null;
  building_types?: string[] | null;
  cap_val: number | string | null;
  cap_val_currency: string;
  close_disposal?: number;
  comment?: string | null;
  completion_date: string | null;
  entire_property: number | boolean;
  floor_units: FloorUnitCreatePayload[];
  heads_of_terms?: FileItem[] | File | null;
  investment: NumberBoolean;
  lease_comment: string | null;
  lease_expiry_month: number | null;
  lease_expiry_year: string | null;
  match_id?: number | null;
  our_acquisition: NumberBoolean;
  our_disposal: NumberBoolean;
  passing_niy?: number;
  passing_rent?: number | null;
  passing_rent_currency?: string;
  passing_rent_metric?: Metric | null;
  publish_to_atlas?: boolean;
  purchase_price: number | string | null;
  purchase_price_currency: string;
  purchaser_agent_basic?: string | null;
  purchaser_agent_users?: string | null;
  purchaser_basic?: string | null;
  purchaser_companies?: number[] | null;
  sale_type: number | null;
  sectors?: Id[];
  size_measure: string | null;
  size_metric: Metric | null;
  size: number | string | null;
  source: number | string | null;
  tags: string | null;
  transaction_notes?: string | null;
  vendor_agent_basic?: string | null;
  vendor_agent_users?: string | null;
  vendor_basic?: string | null;
  vendor_companies?: (number | { name: string })[] | null;
  wault_to_break?: number;
  wault_to_expiry?: number;
}

export interface SaleTransactionsCreateDTO {
  address: AddressPayload;
  details: SaleTransactionsCreateDetails;
  property_id: number | null;
}

export type SaleTransactionsCreatePayload = BodyPayload<SaleTransactionsCreateDTO>;

export interface SaleTransactionCreateCommentDTO {
  comments: string;
}

export interface SaleTransactionCreateCommentPayload extends BodyPayload<SaleTransactionCreateCommentDTO> {
  id: string;
}

export interface SaleTransactionUpdateAddressDTO {
  address: AddressPayload;
  property_id: number | null;
  tags: string | null;
}

export interface SaleTransactionUpdateAddressPayload extends BodyPayload<SaleTransactionUpdateAddressDTO> {
  id: string;
}

export interface SaleTransactionUpdateDTO {
  amenities_specifications: {
    [key: string]: string;
  };
  assigned_users: string;
  branding_main_colour: string;
  branding_secondary_colour: string;
  branding_tertiary_colour: string;
  breeam_rating: string;
  building_grade: string;
  building_types: string[];
  cap_val_currency: string;
  cap_val: number | null;
  class_of_use: string[];
  client_requested_confidentiality: NumberBoolean;
  completion_date: string;
  epc_band: string;
  epc_exempt_reason: number | null;
  epc_exempt: boolean | null;
  epc_multiple_band: string;
  epc_multiple_value: string;
  epc_multiple: boolean;
  epc_value: number | null;
  estimated: NumberBoolean | null;
  internal_confidentiality_date: string | null;
  investment: boolean;
  lease_comment: string;
  lease_expiry_month: number | null;
  lease_expiry_year: number | null;
  legal_fees_applicable: string | null;
  legal_fees_comment: string;
  nda_comments: string | null;
  nda_signed: NumberBoolean;
  our_acquisition: boolean;
  our_disposal: boolean;
  passing_giy: number | null;
  passing_niy: number | null;
  passing_rent_currency: string;
  passing_rent_metric: Metric | null;
  passing_rent: number | null;
  pattern: string | null;
  pertinent_info: string;
  potential_epc_band: string;
  potential_epc_multiple_band: string;
  potential_epc_multiple_value: string;
  potential_epc_multiple: boolean;
  potential_epc_value: number | null;
  public_website_link: string;
  purchase_price_currency: string;
  purchase_price: number | null;
  purchaser_agent_companies: string;
  purchaser_agent_contacts: string;
  purchaser_agent_users: string;
  purchaser_companies: string;
  purchaser_contacts: string;
  purchaser_solicitor_companies: string;
  purchaser_solicitor_contacts: string;
  reason_for_requirement: string[];
  internal_confidentiality: NumberBoolean;
  sale_type: number | null;
  sectors: string[];
  size_measure: string | null;
  size_metric: Metric | null;
  size: number | null;
  source_text: string;
  source: number | null;
  status: Id | null;
  tags: string;
  teams: string;
  tenancy_comment: string | null;
  transaction_status: number | null;
  vat_applicable: string;
  vat_comment: string;
  vendor_agent_companies: string;
  vendor_agent_contacts: string;
  vendor_agent_users: string;
  vendor_companies: string;
  vendor_contacts: string;
  vendor_solicitor_companies: string;
  vendor_solicitor_contacts: string;
  videos: IVideo[];
  wault_to_break: number | null;
  wault_to_expiry: number | null;
}

export interface SaleTransactionsUpdatePayload extends BodyPayload<Partial<SaleTransactionUpdateDTO>> {
  id: string;
}

export interface SaleTransactionPublishToRadiusDTO {
  details: IPublishToRadius['details'];
}

export interface SaleTransactionPublishToRadiusPayload extends BodyPayload<SaleTransactionPublishToRadiusDTO> {
  id: string;
}

export interface SaleTransactionPublishToAtlasDTO {
  details: IPublishToAtlas['details'];
}

export interface SaleTransactionPublishToAtlasPayload extends BodyPayload<SaleTransactionPublishToAtlasDTO> {
  id: string;
}

// ---- Bulk actions (Sales) ----

export interface SaleTransactionsBulkUpdateStatusPayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    status: number;
  };
}

export interface SaleTransactionsBulkUpdateTagsPayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    tags: number[];
  };
}

export interface SaleTransactionsBulkDeletePayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
  };
}

export interface SaleTransactionsBulkUpdateTypePayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    investment: boolean;
  };
}

export interface SaleTransactionsBulkChangeTenurePayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    sale_type: number;
  };
}

export interface SaleTransactionsBulkChangeBuildingTypesPayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    building_types: string[];
  };
}

export interface SaleTransactionsBulkSetOurAcquisitionPayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    our_acquisition: boolean;
  };
}

export interface SaleTransactionsBulkSetOurDisposalPayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    our_disposal: boolean;
  };
}

export interface SaleTransactionsBulkAssignUsersPayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    assigned_users: Id[];
  };
}
export interface SaleTransactionsBulkUpdateTeamsPayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    teams: number[];
  };
}

export interface SaleTransactionsBulkUpdateCompletionDatePayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    completion_date: string;
  };
}

export interface SaleTransactionsBulkUpdateSectorsPayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    sectors: string[];
  };
}

export interface SaleTransactionsBulkUpdateGradeOfSpacePayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    building_grade: string;
  };
}

export interface SaleTransactionsBulkUpdateMeasurementTypePayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    measurement_type: string;
  };
}

export interface SaleTransactionsBulkUpdatePossibleUseClassesPayload {
  body: {
    sale_transaction_ids?: number[];
    filters?: Record<string, any>;
    class_of_use: string[];
  };
}

export type SaleTransactionsBulkPublishToAtlasPayloadDTO = {
  columns: string[];
  details: {
    [key: string]: IPublishToAtlasDetails;
  };
  excluded_ids?: number[];
  filters?: Record<string, any>;
  sale_transaction_ids?: number[];
};

export type SaleTransactionsBulkPublishToAtlasPayload = BodyPayload<SaleTransactionsBulkPublishToAtlasPayloadDTO>;

export interface AtlasValidationError {
  type: string;
  reason: string;
  field: string | null;
  message: string | null;
  value: any;
}

export interface AtlasValidationErrorResponse {
  mandatory_errors: AtlasValidationError[];
  ranking_errors: AtlasValidationError[];
}

export interface SaleTransactionAtlasValidationErrorResponse {
  errors: AtlasValidationErrorResponse;
}

export type SaleTransactionReorderFilesPayloadDTO = {
  asset_ids: string;
  type: number;
};

export interface SaleTransactionReorderFilesPayload extends BodyPayload<SaleTransactionReorderFilesPayloadDTO> {
  assetKey: keyof Pick<
    SaleTransactionsShowItem,
    'heads_of_terms' | 'brochures' | 'floor_plans' | 'epcs' | 'other_files' | 'images' | 'logo' | 'private_files'
  >;
  id: string;
}

export interface SaleTransactionDeleteFilePayload {
  assetId: string;
  assetKey: keyof Pick<
    SaleTransactionsShowItem,
    'heads_of_terms' | 'brochures' | 'floor_plans' | 'epcs' | 'other_files' | 'images' | 'logo' | 'private_files'
  >;
  id: string;
}

export type SaleTransactionRenameFileDTO = {
  name: string;
};

export interface SaleTransactionRenameFilePayload extends BodyPayload<SaleTransactionRenameFileDTO> {
  assetId: string;
  assetKey: keyof Pick<
    SaleTransactionsShowItem,
    'heads_of_terms' | 'brochures' | 'floor_plans' | 'epcs' | 'other_files' | 'images' | 'logo' | 'private_files'
  >;
  id: string;
}

export interface SaleTransactionValidatePublishPreCreateParams {
  request_source: 'disposal' | 'create_form' | 'requirement';
  completion_date: string;
  building_types: string[];
  disposal_id?: string | null;
  acquisition_id?: string;
  match_id?: string;
  our_acquisition?: boolean;
  our_disposal?: boolean;
  property_id?: number;
  purchase_price?: number | string;
  purchase_price_currency?: string;
}

const saleTransactionActivitiesIndexParamKeys = [
  'activity_types',
  'created_from',
  'created_to',
  'limit',
  'page',
] as const;

export type SaleTransactionActivitiesIndexParams = PartialQuery<typeof saleTransactionActivitiesIndexParamKeys>;

export interface SaleTransactionActivitiesIndexPayload extends QueryPayload<SaleTransactionActivitiesIndexParams> {
  saleTransactionId: string;
}

export interface SaleTransactionActivitiesIndexResponse {
  data: ActivityIndexItem[];
  meta: {
    pagination: ListResponsePagination;
  };
}

export interface SpaceDetailsItemDTO {
  id?: Id | null;
  floor: string;
  building_types: string | null;
  unit: string;
  part_floor: boolean;
  size_metric: Metric;
  size_value: number;
}

export interface SaleTransactionsSpaceDetailsUpdatePayload
  extends BodyPayload<Partial<TransactionsSpaceDetailsUpdateDTO>> {
  id: string;
}
